// Which tabs the till's menu shows, and which dishes sit under each.
//
// Drinks and kids' dishes are each gathered under one tab with their own sub-tabs (Lagers,
// Wines… / Starters, Mains…). The groups were defined by their sub-categories only, so a
// section literally called "drinks" — the seeded menu has one, and plenty of venues would —
// counted as an ordinary section *and* triggered the Drinks group. Two tabs called Drinks,
// with the same React key, which is undefined behaviour: on the till it rendered three, all
// highlighted together, and Desserts vanished from the strip. Both Drinks tabs then showed
// the sub-categories and not the "drinks" section itself, so its dishes could only be found
// by scrolling All Items. The same was waiting for a section called "kids".
//
// A group's own name belongs to the group.

import { courseForCategory } from './courses';

export const DRINK_SUBS = ['lagers', 'wines', 'cocktails', 'soft_drinks', 'shakes'];
export const KIDS_SUBS = ['kids_starters', 'kids_mains', 'kids_drinks', 'kids_desserts'];

export const isDrink = (cat) => cat === 'drinks' || DRINK_SUBS.includes(cat);
export const isKids = (cat) => cat === 'kids' || KIDS_SUBS.includes(cat);

// A drinks section, keyed (`lagers`) or named in the back office ("Beer & cider", "Soft
// drinks"). Named ones used to keep tabs of their own: there was no Drinks tab, the Bar page
// opened on one that wasn't in the strip, and picking Soft Drinks lost "All Drinks" with no
// way back to it (Martin, Oct 2026). They're Drinks' sub-tabs now, like the keyed ones.
export const isDrinkSection = (cat) => !isKids(cat) && (isDrink(cat) || courseForCategory(cat) === 'drinks');

/** The Drinks tab's sub-tabs: every drinks section on the menu but the group's own name. */
export const drinkSubsOf = (rawCategories) => [...new Set(rawCategories || [])].filter((c) => c !== 'drinks' && isDrinkSection(c));

/** The tab strip, each tab once: All, the ordinary sections in menu order, then the groups. */
export const tabsFor = (rawCategories, byOrder = () => 0) => {
  const cats = [...new Set(rawCategories || [])];
  const ordinary = cats.filter((c) => !isDrinkSection(c) && !isKids(c)).sort(byOrder);
  return [
    'all',
    ...ordinary,
    ...(cats.some(isDrinkSection) ? ['drinks'] : []),
    ...(cats.some(isKids) ? ['kids'] : []),
  ];
};

/** Does a dish in `category` belong under `tab` (and, inside a group, under `sub`)? */
export const inTab = (category, tab, sub = null) => {
  if (tab === 'all') return true;
  // Every drink, including a section the back office named ("Beer & cider", "Wine").
  if (tab === 'drinks') return sub ? category === sub : isDrinkSection(category);
  if (tab === 'kids') return sub ? category === sub : isKids(category);
  return category === tab;
};

// The Bar (Oct 2026, Chris's review): one row of tabs, all about drinks. All drinks, each
// drinks section, then anything else the bar sells (crisps, nuts) under one tab at the end,
// rather than the food strip above a second row of drinks.
export const OTHER = 'other';

export const barTabsFor = (rawCategories, byOrder = () => 0) => {
  const cats = [...new Set(rawCategories || [])];
  return [
    'drinks',
    ...drinkSubsOf(cats).sort(byOrder),
    ...(cats.some((c) => !isDrinkSection(c)) ? [OTHER] : []),
  ];
};

export const inBarTab = (category, tab) => {
  if (tab === 'drinks') return isDrinkSection(category);
  if (tab === OTHER) return !isDrinkSection(category);
  return category === tab;
};
